/* ══════════════════════════════════════════════════════════════
   selectSymptoms.utils.js
   Voice hook + keyword matching helper
══════════════════════════════════════════════════════════════ */

import { useState, useRef, useCallback } from "react";
import { ALL_HEALTH_KEYWORDS } from "./SelectSymptomsConstants";

/* ─── keyword matcher ─────────────────────────────────────── */
export function matchHealthKeywords(transcript) {
  const lower = transcript.toLowerCase();
  const matched = [];
  ALL_HEALTH_KEYWORDS.forEach(kw => {
    if (lower.includes(kw)) {
      const label = kw.replace(/\b\w/g, c => c.toUpperCase());
      if (!matched.find(m => m.label === label))
        matched.push({ id: Date.now() + Math.random(), label });
    }
  });
  return matched;
}

/* ─── voice hook ──────────────────────────────────────────── */
export function useVoiceSymptoms({ onDetected }) {
  const [isListening, setIsListening] = useState(false);
  const [liveText, setLiveText] = useState("");
  const [micError, setMicError] = useState("");
  const recRef = useRef(null);
  const silenceRef = useRef(null);

  const supported =
    typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  const stop = useCallback(() => {
    recRef.current?.stop();
    setIsListening(false);
    clearTimeout(silenceRef.current);
  }, []);

  const start = useCallback(() => {
    if (!supported) { setMicError("Voice input not supported in this browser."); return; }
    setMicError(""); setLiveText("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    recRef.current = rec;

    rec.onresult = e => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++)
        interim += e.results[i][0].transcript;
      setLiveText(interim);
      clearTimeout(silenceRef.current);
      silenceRef.current = setTimeout(() => {
        if (interim.trim()) onDetected(interim.trim());
        setLiveText(""); stop();
      }, 1800);
    };

    rec.onerror = e => {
      setMicError(
        e.error === "not-allowed"
          ? "Microphone access denied."
          : `Error: ${e.error}`
      );
      setIsListening(false);
    };

    rec.onend = () => setIsListening(false);
    rec.start(); setIsListening(true);
  }, [supported, onDetected, stop]);

  const toggleMic = useCallback(
    () => (isListening ? stop() : start()),
    [isListening, stop, start]
  );

  return { isListening, liveText, micError, toggleMic, supported };
}

/* ─── symptom set helpers ─────────────────────────────────── */
export function addSymptom(prev, label) {
  return prev.find(s => s.label === label)
    ? prev
    : [...prev, { id: Date.now() + Math.random(), label }];
}

export function removeSymptom(prev, label) {
  return prev.filter(s => s.label !== label);
}

export function toggleSymptom(prev, label) {
  return prev.find(s => s.label === label)
    ? prev.filter(s => s.label !== label)
    : [...prev, { id: Date.now() + Math.random(), label }];
}